import { useApplyEdition } from '@/pages/Apply/components/use-apply-edition';
import type { ApplyPerson } from '@/types';
import { EventLabel } from './EventLabel';
import { LeaveTeamButton } from './LeaveTeamButton';

interface Props {
  leader: ApplyPerson;
  members: ApplyPerson[];
  modality: 'team' | 'team_looking' | 'solo';
  status: string;
  viewerRole: 'leader' | 'member';
  leaderWillTransfer: boolean;
}

export function StatusView({
  leader,
  members,
  modality,
  status,
  viewerRole,
  leaderWillTransfer,
}: Props) {
  const { eventCity } = useApplyEdition();
  const isTeamLooking = modality === 'team_looking';
  const isRejected = status === 'rejected';
  const isSubmitted = status !== 'draft';

  const heading = isRejected
    ? 'Postulación no seleccionada'
    : viewerRole === 'member'
      ? `Estás en el equipo de @${leader.githubHandle}`
      : isSubmitted
        ? 'Postulación enviada'
        : 'Tu postulación';

  const subheading = isRejected
    ? `Gracias por aplicar a Platanus Hack 26: ${eventCity}.`
    : viewerRole === 'member'
      ? isSubmitted
        ? 'El equipo ya envió la postulación. Te avisaremos por correo cuando tengamos novedades.'
        : 'Cuando el líder envíe la postulación, te avisaremos por correo.'
      : 'Te avisaremos por correo cuando tengamos novedades.';

  return (
    <div className="mx-auto max-w-xl space-y-8 px-4 py-12">
      <header className="space-y-2 text-center">
        <p className="text-muted-foreground text-sm">
          Postulación a <EventLabel />
        </p>
        <h1 className="font-bold text-2xl uppercase tracking-wide sm:text-3xl">
          {heading}
        </h1>
        <p className="text-muted-foreground text-sm">{subheading}</p>
      </header>

      <section className="space-y-3">
        <h2 className="font-bold text-sm uppercase tracking-wide">
          {isTeamLooking ? 'Postulación solo' : 'Equipo'}
        </h2>
        <ul className="space-y-2">
          <li className="border-2 border-primary bg-background/80 p-3">
            <p className="font-bold">
              {leader.fullName}
              {leader.isViewer && ' (tú)'}
            </p>
            <p className="text-muted-foreground text-xs">
              @{leader.githubHandle} · Líder
            </p>
          </li>
          {members.map((m) => (
            <li
              key={m.githubHandle}
              className="border-2 border-input bg-background/40 p-3"
            >
              <p className="font-bold">
                {m.fullName}
                {m.isViewer && ' (tú)'}
              </p>
              <p className="text-muted-foreground text-xs">@{m.githubHandle}</p>
            </li>
          ))}
        </ul>
      </section>

      {!isRejected && (
        <div className="flex justify-center">
          <LeaveTeamButton
            viewerRole={viewerRole}
            willTransfer={leaderWillTransfer}
            leaderHandle={leader.githubHandle}
          />
        </div>
      )}
    </div>
  );
}
